/** Sezione STAT: si apre solo a partita finita, contiene le risposte.
 *
 * Chart.js e' bundlato da Vite (import), mai da CDN. */

import Chart from "chart.js/auto";
import type { RiepilogoRound } from "../../worker/src/protocol";

// palette categoriale, ordine fisso: il colore segue la persona, mai la posizione
const SERIE = ["#3987e5", "#d95926", "#199e70", "#c98500", "#d55181"];
const INK = "#c3c2b7";
const GRIGLIA = "#33332f";

Chart.defaults.color = INK;
Chart.defaults.font.family = "system-ui, -apple-system, sans-serif";
Chart.defaults.borderColor = GRIGLIA;
Chart.defaults.animation = false;

const esc = (s: string) =>
  s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);
const num = (n: number) => n.toLocaleString("it-IT");

interface Dati {
  meta: { persone: string[] };
  base: { totale_messaggi: number; giorni_coperti: number; giorni_con_messaggi: number; da: string; a: string; per_persona: Record<string, { messaggi: number; quota_pct: number; len_media: number }> };
  tempo: { per_ora_totale: number[] };
  timeline: { serie_mensile: Record<string, number>[]; mese_record: { mese: string; messaggi: number }; silenzi_piu_lunghi: { ore: number }[] };
  reactions: { reaction_per_100_messaggi: Record<string, number> };
}

function colore(persone: string[], p: string): string {
  return SERIE[persone.indexOf(p) % SERIE.length];
}

function tela(root: HTMLElement, titolo: string, nota = ""): HTMLCanvasElement {
  const box = document.createElement("div");
  box.className = "gr";
  box.innerHTML = `<h2 style="margin:4px 0 10px">${esc(titolo)}</h2>${nota ? `<small>${esc(nota)}</small>` : ""}`;
  const c = document.createElement("canvas");
  c.height = 220;
  box.appendChild(c);
  root.appendChild(box);
  return c;
}

export function renderStats(root: HTMLElement, datiRaw: unknown, domande: RiepilogoRound[], io: string) {
  const d = datiRaw as Dati;
  const persone = d.meta.persone;

  root.innerHTML = `
    <div class="riga" style="justify-content:space-between">
      <h1 style="margin:0">Statistiche</h1>
      <button class="fantasma" id="indietro" style="width:auto">Indietro</button>
    </div>
    <p>Dal ${d.base.da.slice(0, 10)} al ${d.base.a.slice(0, 10)}.</p>
    <div class="tiles">
      <div class="tile"><div class="n">${num(d.base.totale_messaggi)}</div><div class="k">messaggi</div></div>
      <div class="tile"><div class="n">${num(d.base.giorni_con_messaggi)}</div><div class="k">giorni parlati su ${num(d.base.giorni_coperti)}</div></div>
      <div class="tile"><div class="n">${num(d.timeline.mese_record.messaggi)}</div><div class="k">record: ${esc(d.timeline.mese_record.mese)}</div></div>
      <div class="tile"><div class="n">${Math.round(d.timeline.silenzi_piu_lunghi[0].ore)}h</div><div class="k">silenzio piu' lungo</div></div>
    </div>`;

  // 1. quanto scrive ognuno: magnitudine per identita' -> barre orizzontali etichettate
  const perPersona = persone.map((p) => d.base.per_persona[p]?.messaggi ?? 0);
  new Chart(tela(root, "Messaggi per persona"), {
    type: "bar",
    data: {
      labels: persone,
      datasets: [{
        label: "messaggi",
        data: perPersona,
        backgroundColor: persone.map((p) => colore(persone, p)),
        borderRadius: 4,
        borderSkipped: "start",
      }],
    },
    options: {
      indexAxis: "y",
      responsive: true,
      plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => ` ${num(c.parsed.x ?? 0)} messaggi` } } },
      scales: {
        x: { grid: { color: GRIGLIA }, ticks: { callback: (v) => num(Number(v)) } },
        y: { grid: { display: false } },
      },
    },
  });

  // 2. la giornata del gruppo: una sola serie, nessuna legenda
  new Chart(tela(root, "Il gruppo ora per ora", "somma di otto anni"), {
    type: "line",
    data: {
      labels: [...Array(24).keys()].map((h) => `${h}`),
      datasets: [{
        label: "messaggi",
        data: d.tempo.per_ora_totale,
        borderColor: SERIE[0],
        backgroundColor: "transparent",
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 8,
        tension: 0.3,
      }],
    },
    options: {
      responsive: true,
      interaction: { mode: "index", intersect: false },
      plugins: { legend: { display: false }, tooltip: { callbacks: { title: (c) => `ore ${c[0].label}`, label: (c) => ` ${num(c.parsed.y ?? 0)} messaggi` } } },
      scales: { x: { grid: { display: false } }, y: { grid: { color: GRIGLIA }, ticks: { callback: (v) => num(Number(v)) } } },
    },
  });

  // 3. otto anni, mese per mese
  const serie = d.timeline.serie_mensile;
  new Chart(tela(root, "Mese per mese"), {
    type: "line",
    data: {
      labels: serie.map((r) => String(r.mese)),
      datasets: persone.map((p) => ({
        label: p,
        data: serie.map((r) => Number(r[p] ?? 0)),
        borderColor: colore(persone, p),
        backgroundColor: "transparent",
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 8,
        tension: 0.25,
      })),
    },
    options: {
      responsive: true,
      interaction: { mode: "index", intersect: false },
      plugins: { legend: { display: true, labels: { boxWidth: 10, boxHeight: 10, usePointStyle: true } } },
      scales: {
        x: { grid: { display: false }, ticks: { maxTicksLimit: 6, callback(_v, i) { return String(serie[i]?.mese ?? "").slice(0, 4); } } },
        y: { grid: { color: GRIGLIA } },
      },
    },
  });

  // 4. chi mette i cuori
  const r100 = persone.map((p) => d.reactions.reaction_per_100_messaggi[p] ?? 0);
  new Chart(tela(root, "Reaction ricevute ogni 100 messaggi"), {
    type: "bar",
    data: {
      labels: persone,
      datasets: [{ label: "per 100", data: r100, backgroundColor: persone.map((p) => colore(persone, p)), borderRadius: 4 }],
    },
    options: {
      responsive: true,
      plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => ` ${c.parsed.y} ogni 100` } } },
      scales: { x: { grid: { display: false } }, y: { grid: { color: GRIGLIA } } },
    },
  });

  // vista tabellare: gli stessi numeri senza dipendere dal colore
  const tab = document.createElement("div");
  tab.className = "gr";
  tab.innerHTML = `<h2 style="margin:4px 0 10px">I numeri</h2>
    <table class="dati"><thead><tr><th>persona</th><th class="n">messaggi</th><th class="n">quota</th><th class="n">len media</th><th class="n">reaction/100</th></tr></thead>
    <tbody>${persone
      .map((p) => {
        const b = d.base.per_persona[p];
        return `<tr><td>${esc(p)}</td><td class="n">${num(b.messaggi)}</td><td class="n">${b.quota_pct}%</td><td class="n">${b.len_media}</td><td class="n">${d.reactions.reaction_per_100_messaggi[p] ?? "-"}</td></tr>`;
      })
      .join("")}</tbody></table>`;
  root.appendChild(tab);

  // le risposte della partita appena finita
  const rip = document.createElement("div");
  rip.innerHTML =
    `<h2>Le domande di stasera</h2>` +
    domande
      .map((q) => {
        const mia = q.esiti.find((e) => e.id === io);
        const testo = Array.isArray(q.risposta) ? q.risposta.join(" › ") : q.risposta;
        const tua = mia?.risposta == null ? "niente" : Array.isArray(mia.risposta) ? mia.risposta.join(" › ") : mia.risposta;
        return `<div class="rev">
          <div class="q">${esc(q.domanda)}</div>
          ${q.frase ? `<div><em>"${esc(q.frase)}"</em></div>` : ""}
          <div class="a">${esc(testo)}</div>
          <small>${esc(q.reveal)}</small>
          ${mia ? `<div><small>tu: ${esc(tua)} · ${mia.punti} punti</small></div>` : ""}
        </div>`;
      })
      .join("");
  root.appendChild(rip);
}
